import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getPageSeoSettings } from '../lib/seoManager';

interface SEOHeadProps {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'college';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  pageKey?: string; // For dynamic SEO loading
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  pageKey
}) => {
  const [dynamicSeo, setDynamicSeo] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  
  React.useEffect(() => {
    if (pageKey) {
      setLoading(true);
      console.log(`Loading SEO for page: ${pageKey}`);
      getPageSeoSettings(pageKey).then(seo => {
        console.log(`SEO loaded for ${pageKey}:`, seo);
        setDynamicSeo(seo);
        setLoading(false);
      }).catch(error => {
        console.error(`Error loading SEO for ${pageKey}:`, error);
        setLoading(false);
      });
    }
  }, [pageKey]);
  
  // Use dynamic SEO if available, otherwise use props
  const finalTitle = dynamicSeo?.title || title;
  const finalDescription = dynamicSeo?.description || description;
  const finalKeywords = dynamicSeo?.keywords || keywords;
  const finalImage = dynamicSeo?.image || image;
  const finalCanonicalUrl = dynamicSeo?.canonical_url || url;
  const finalOgTitle = dynamicSeo?.og_title || finalTitle;
  const finalOgDescription = dynamicSeo?.og_description || finalDescription;
  const finalTwitterTitle = dynamicSeo?.twitter_title || finalTitle;
  const finalTwitterDescription = dynamicSeo?.twitter_description || finalDescription;
  
  const siteUrl = 'https://gradvikalp.com';
  const fullUrl = finalCanonicalUrl ? `${siteUrl}${finalCanonicalUrl}` : siteUrl;
  const defaultImage = `${siteUrl}/Grad-Vikalp.png`;
  const socialImage = finalImage || defaultImage;

  // Don't render until we have the final SEO data (prevents default SEO from flashing)
  if (pageKey && loading) {
    return null;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      {finalDescription && <meta name="description" content={finalDescription} />}
      {finalKeywords && <meta name="keywords" content={finalKeywords} />} 
       
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalOgTitle} />
      {finalOgDescription && <meta property="og:description" content={finalOgDescription} />}
      <meta property="og:image" content={socialImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Grad Vikalp" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTwitterTitle} />
      {finalTwitterDescription && <meta name="twitter:description" content={finalTwitterDescription} />}
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:site" content="@gradvikalp" />
      
      {/* Article specific meta tags */}
      {type === 'article' && author && <meta name="author" content={author} />}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

export default SEOHead;